import instantsearch from 'instantsearch.js';
import { configure, searchBox, clearRefinements, hits } from 'instantsearch.js/es/widgets';
import { history } from 'instantsearch.js/es/lib/routers';
import searchClient from '@pratt/js/components/algolia/search-client';
import { customPagination } from '@pratt/js/components/algolia/pagination';
import { refinementListWithPanel } from '@pratt/js/components/algolia/panel';
import { HitHelpers } from '@pratt/js/components/algolia/hit-helpers';
import { refinementListTemplate } from '@pratt/js/components/algolia/templates/refinement-list';

class ResourcePolicyServiceSearch {
  constructor(rootEl) {
    this.rootEl = rootEl;
    if (!this.rootEl) {
      throw new Error('Root resource/policy/service search element not found.');
    }

    this.hitsPerPage = 24;

    this.search = instantsearch({
      indexName: this.rootEl.dataset['algoliaIndex'],
      searchClient: searchClient,
      routing: {
        router: history({
          cleanUrlOnDispose: true,
        }),
      },
      insights: true,
      future: {
        preserveSharedStateOnUnmount: true,
      },
    });

    this.init();
    this.search.start();
  }

  init() {
    this.search.addWidgets([
      // https://www.algolia.com/doc/api-reference/widgets/configure/js/
      // Parameter options: https://www.algolia.com/doc/api-reference/search-api-parameters/
      configure({
        hitsPerPage: this.hitsPerPage,
        distinct: true,
        // Force faceting to be applied after de-duplication (via the Distinct setting)
        facetingAfterDistinct: true,
        // Filter results by 'type' facet
        // https://www.algolia.com/doc/api-reference/api-parameters/filters/#facet-filters
        filters: `type:"resource" OR type:"policy" OR type:"service"`,
      }),

      searchBox({
        container: '.js-resource-policy-service-search-input',
        placeholder: 'Search',
        showSubmit: false,
      }),

      clearRefinements({
        container: '.js-resource-policy-service-search-clear',
        templates: {
          resetLabel: 'Reset',
        },
      }),

      refinementListWithPanel('Type')({
        container: '.js-resource-policy-service-search-type-input',
        attribute: 'type',
        operator: 'and',
        sortBy: ['name:asc'],
        templates: {
          item: refinementListTemplate,
        },
        transformItems: items =>
          items.map(item => ({
            ...item,
            highlighted: HitHelpers.getTypeDisplay(item.highlighted),
          })),
      }),

      refinementListWithPanel('School')({
        container: '.js-resource-policy-service-search-schools-input',
        attribute: 'schools',
        operator: 'and',
        sortBy: ['name:asc'],
        templates: {
          item: refinementListTemplate,
        },
      }),

      refinementListWithPanel('Department')({
        container: '.js-resource-policy-service-search-departments-input',
        attribute: 'departments',
        operator: 'and',
        limit: 10,
        showMore: true,
        showMoreLimit: 100,
        sortBy: ['name:asc'],
        templates: {
          item: refinementListTemplate,
        },
      }),

      refinementListWithPanel('Office')({
        container: '.js-resource-policy-service-search-offices-input',
        attribute: 'offices',
        operator: 'and',
        limit: 10,
        showMore: true,
        showMoreLimit: 100,
        sortBy: ['name:asc'],
        templates: {
          item: refinementListTemplate,
        },
      }),

      refinementListWithPanel('Audience')({
        container: '.js-resource-policy-service-search-audiences-input',
        attribute: 'audiences',
        operator: 'and',
        sortBy: ['name:asc'],
        templates: {
          item: refinementListTemplate,
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/hits/js/
      hits({
        container: '.js-resource-policy-service-search-hits',
        templates: {
          item(hit) {
            return `
              <div class="archive-hit archive-hit--${hit.type}">
                <div class="archive-hit-inner">
                  <div class="archive-hit__titles">
                    <p class="archive-hit__type">${HitHelpers.getTypeDisplay(hit.type)}</p>
                    <div class="archive-hit__title">
                      <a class="archive-hit__link" href="${hit.url}" onClick="sendEvent('click','${hit.type} clicked', '${hit.objectID}', '${hit.__queryID}', ${hit.__position})">
                        <span class="archive-hit__title-text">${hit.title}</span>
                      </a>
                    </div>
                  </div>
                  <p class="archive-hit__post">${hit.excerpt}</p>

                ${hit.thumbnail ? `
                  <div class="archive-hit__image">
                    <figure class="archive-hit__figure">
                      <img class="archive-hit__img" src="${hit.thumbnail}" alt="${hit.title}" />
                    </figure>
                  </div>
                ` : ''}
                </div>

              ${hit.schools?.length || hit.departments?.length || hit.offices?.length ? `
                <div class="archive-hit__meta">
                    ${
                      hit.schools?.length
                        ? `
                      <p class="archive-hit__school">${hit.schools.join('; ')}</p>
                    `
                        : ''
                    }
                    ${
                      hit.departments?.length
                        ? `
                      <p class="archive-hit__department">${hit.departments.join('; ')}</p>
                    `
                        : ''
                    }
                    ${
                      hit.offices?.length
                        ? `
                      <p class="archive-hit__department">${hit.offices.join('; ')}</p>
                    `
                        : ''
                    }
                  </div>
                </div>` : ''
              }

              </div>
            `;
          },
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/pagination/js/
      customPagination({
        container: document.querySelector('.js-resource-policy-service-search-pagination'),
      }),
    ]);
  }
}

export default ResourcePolicyServiceSearch;
